// Utility helpers for exporting chat conversations

import { PDFDocument, StandardFonts, rgb, PDFFont, PDFPage } from 'pdf-lib';

// PDF rendering configuration
const PDF_CONFIG = {
  pageSize: [595.28, 841.89] as [number, number], // A4 en puntos
  margin: 40,
  fontSize: 12,
  lineHeight: 18,
  tableCellPaddingX: 6,
  tableCellPaddingY: 5,
  initialY: 800,
  minY: 60,
  colors: {
    text: rgb(0, 0, 0),
    sender: rgb(0.2, 0.2, 0.2),
    separator: rgb(0.7, 0.7, 0.7),
    tableBorder: rgb(0.75, 0.75, 0.75),
    tableHeaderBg: rgb(0.95, 0.95, 0.95),
  }
};

interface PDFContext {
  document: PDFDocument;
  page: PDFPage;
  font: PDFFont;
  fontBold: PDFFont;
  currentY: number;
}

/**
 * Removes basic markdown syntax while preserving line breaks for TXT exports.
 */
export function cleanMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/^\s*[-*+] /gm, '  - ')
    .replace(/^\s*\d+\. /gm, (match) => '  ' + match.trim())
    .replace(/\n{2,}/g, '\n')
    .replace(/\r/g, '')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1 ($2)')
    .replace(/_/g, '')
    .replace(/#+\s?/g, '')
    .trim();
}

/**
 * Applies minimal markdown replacements to HTML-like strings.
 */
function applyMarkdownTransforms(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]*)`/g, '<code>$1</code>')
    .replace(/^\s*[-*+] (.*)$/gm, '<li>$1</li>')
    .replace(/^\s*\d+\. (.*)$/gm, '<li>$1</li>')
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/\n/g, '<br>')
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>')
    .replace(/#+\s?(.*)/g, '<strong>$1</strong>');
}

/**
 * Wraps transformed markdown in block tags and wraps list items inside <ul>.
 */
function wrapInHtmlStructure(html: string): string {
  const wrapped = `<p>${html}</p>`;
  // Wrap list items inside unordered list tags
  return wrapped.replace(/(<li>.*?<\/li>)/gs, '<ul>$1</ul>');
}

/**
 * Converts markdown text into a simplified HTML string for PDF rendering.
 */
export function markdownToHtml(md: string): string {
  const transformed = applyMarkdownTransforms(md);
  return wrapInHtmlStructure(transformed);
}

/**
 * Normalize text to avoid characters that StandardFonts (WinAnsi) cannot encode.
 * Replaces common dash/space characters that often cause encoding errors.
 */
function sanitizeForPdf(text: string): string {
  if (!text) return text;
  // Normalize compatibility characters first
  let t = text.normalize('NFKC');
  // Replace common mathematical and symbolic characters with ASCII-safe equivalents
  t = t
    .replace(/≈/g, '~')
    .replace(/≠/g, '!=')
    .replace(/≤/g, '<=')
    .replace(/≥/g, '>=')
    .replace(/±/g, '+/-')
    .replace(/×/g, 'x')
    .replace(/÷/g, '/')
    .replace(/√/g, 'sqrt')
    .replace(/∞/g, 'infinity')
    .replace(/π/g, 'pi')
    .replace(/∑/g, 'sum')
    .replace(/∏/g, 'product')
    .replace(/[←↔]/g, '<->')
    .replace(/→/g, '->')
    .replace(/•/g, '-')
    .replace(/…/g, '...');
  // Replace smart punctuation with basic ASCII
  t = t
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'");
  // Replace non-breaking hyphen (U+2011) with normal hyphen
  t = t.replace(/\u2011/g, '-');
  // Replace en-dash and em-dash with hyphen
  t = t.replace(/[\u2013\u2014]/g, '-');
  // Replace non-breaking space with regular space
  t = t.replace(/\u00A0/g, ' ');
  // Replace narrow no-break space (U+202F) and other troublesome spaces
  t = t.replace(/\u202F/g, ' ');
  // Final fallback for any remaining unsupported Unicode (e.g. emoji, symbols)
  t = Array.from(t)
    .map((char) => {
      const code = char.codePointAt(0);
      if (code === undefined) return '?';

      const isAllowedControl = code === 9 || code === 10 || code === 13;
      const isWinAnsiRange = code >= 0x20 && code <= 0xff;

      return isAllowedControl || isWinAnsiRange ? char : '?';
    })
    .join('');
  return t;
}

/**
 * Creates a brand-new PDF context with the configured defaults.
 */
async function createPdfDocument(): Promise<PDFContext> {
  const document = await PDFDocument.create();
  const page = document.addPage(PDF_CONFIG.pageSize);
  const font = await document.embedFont(StandardFonts.Helvetica);
  const fontBold = await document.embedFont(StandardFonts.HelveticaBold);
  
  return {
    document,
    page,
    font,
    fontBold,
    currentY: PDF_CONFIG.initialY
  };
}

/**
 * Ensures there is room in the current page; otherwise starts a new one.
 */
function ensurePageSpace(context: PDFContext): PDFContext {
  if (context.currentY < PDF_CONFIG.margin + PDF_CONFIG.lineHeight) {
    const newPage = context.document.addPage(PDF_CONFIG.pageSize);
    return {
      ...context,
      page: newPage,
      currentY: PDF_CONFIG.initialY
    };
  }
  return context;
}

/**
 * Ensures a block with required height fits on the current page.
 */
function ensureVerticalSpace(context: PDFContext, requiredHeight: number): PDFContext {
  if (context.currentY - requiredHeight < PDF_CONFIG.margin) {
    const newPage = context.document.addPage(PDF_CONFIG.pageSize);
    return {
      ...context,
      page: newPage,
      currentY: PDF_CONFIG.initialY
    };
  }
  return context;
}

/**
 * Breaks long text into lines constrained by the available page width.
 */
function wrapTextToLines(text: string, maxWidth: number, font: PDFFont, fontSize: number): string[] {
  const safeText = sanitizeForPdf(text);
  const words = safeText.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const width = font.widthOfTextAtSize(testLine, fontSize);
    
    if (width <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        lines.push(word);
      }
    }
  }
  
  if (currentLine) {
    lines.push(currentLine);
  }
  
  return lines;
}

/**
 * Splits a markdown table row into individual cells while respecting escaped pipes.
 */
function parseMarkdownTableRow(row: string): string[] {
  const trimmed = row.trim().replace(/^\|/, '').replace(/\|$/, '');
  const cells: string[] = [];
  let current = '';

  for (let i = 0; i < trimmed.length; i++) {
    const char = trimmed[i];

    if (char === '|' && trimmed[i - 1] !== '\\') {
      cells.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  cells.push(current.trim());
  return cells;
}

/**
 * Returns true when a line is a markdown table separator row.
 */
function isTableSeparatorLine(line: string): boolean {
  return /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);
}

/**
 * Detects markdown table blocks and returns their row matrix.
 */
function parseMarkdownTable(lines: string[], startIndex: number): { rows: string[][]; endIndex: number } | null {
  if (startIndex + 1 >= lines.length) return null;

  const header = lines[startIndex];
  const separator = lines[startIndex + 1];

  if (!header.includes('|') || !isTableSeparatorLine(separator)) {
    return null;
  }

  const rows: string[][] = [parseMarkdownTableRow(header)];
  let endIndex = startIndex + 2;

  while (endIndex < lines.length) {
    const line = lines[endIndex];
    if (!line.trim() || !line.includes('|') || isTableSeparatorLine(line)) {
      break;
    }
    rows.push(parseMarkdownTableRow(line));
    endIndex += 1;
  }

  if (rows.length < 1) return null;
  return { rows, endIndex };
}

/**
 * Decodes common HTML entities often present in markdown table cells.
 */
function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

/**
 * Converts markdown/html fragments from table cells into plain text.
 */
function normalizeTableCellText(text: string): string {
  const decoded = decodeHtmlEntities(text)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, ' ');
  return sanitizeForPdf(cleanMarkdown(decoded));
}

/**
 * Wraps multiline cell content into visual lines constrained by cell width.
 */
function wrapCellText(text: string, width: number, font: PDFFont): string[] {
  const sourceLines = text.split(/\n/);
  const wrapped: string[] = [];

  for (const sourceLine of sourceLines) {
    const chunks = wrapTextToLines(sourceLine, width, font, PDF_CONFIG.fontSize);
    if (chunks.length === 0) {
      wrapped.push('');
    } else {
      wrapped.push(...chunks);
    }
  }

  return wrapped;
}

/**
 * Draws a markdown table with borders and bold header row.
 */
function drawMarkdownTable(context: PDFContext, rows: string[][]): PDFContext {
  if (rows.length === 0) return context;

  const columnCount = Math.max(...rows.map((row) => row.length));
  const tableWidth = PDF_CONFIG.pageSize[0] - (PDF_CONFIG.margin * 2);
  const columnWidth = tableWidth / columnCount;
  const textWidth = columnWidth - (PDF_CONFIG.tableCellPaddingX * 2);

  const normalizedRows = rows.map((row) => {
    const filled = [...row];
    while (filled.length < columnCount) {
      filled.push('');
    }
    return filled.map(normalizeTableCellText);
  });

  let updatedContext = context;

  normalizedRows.forEach((row, rowIndex) => {
    const isHeader = rowIndex === 0;
    const cellLines = row.map((cell) =>
      wrapCellText(cell, textWidth, isHeader ? updatedContext.fontBold : updatedContext.font)
    );
    const maxLines = Math.max(1, ...cellLines.map((lines) => lines.length));
    const rowHeight = (maxLines * PDF_CONFIG.lineHeight) + (PDF_CONFIG.tableCellPaddingY * 2);

    updatedContext = ensureVerticalSpace(updatedContext, rowHeight);

    row.forEach((_, colIndex) => {
      const x = PDF_CONFIG.margin + (colIndex * columnWidth);
      const y = updatedContext.currentY - rowHeight;

      updatedContext.page.drawRectangle({
        x,
        y,
        width: columnWidth,
        height: rowHeight,
        borderColor: PDF_CONFIG.colors.tableBorder,
        borderWidth: 0.8,
        color: isHeader ? PDF_CONFIG.colors.tableHeaderBg : undefined,
      });

      const lines = cellLines[colIndex];
      let textY = updatedContext.currentY - PDF_CONFIG.tableCellPaddingY - PDF_CONFIG.fontSize;
      for (const line of lines) {
        updatedContext.page.drawText(line, {
          x: x + PDF_CONFIG.tableCellPaddingX,
          y: textY,
          size: PDF_CONFIG.fontSize,
          font: isHeader ? updatedContext.fontBold : updatedContext.font,
          color: PDF_CONFIG.colors.text,
        });
        textY -= PDF_CONFIG.lineHeight;
      }
    });

    updatedContext.currentY -= rowHeight;
  });

  updatedContext.currentY -= PDF_CONFIG.lineHeight / 2;
  return updatedContext;
}
/**
 * Renders a bullet list item, wrapping text across lines.
 */
function drawBulletLine(context: PDFContext, line: string): PDFContext {
  const cleanLine = line.replace(/^\s*[-*+] /, '');
  const maxWidth = PDF_CONFIG.pageSize[0] - (PDF_CONFIG.margin * 2) - 15;
  const wrappedLines = wrapTextToLines(cleanLine, maxWidth, context.font, PDF_CONFIG.fontSize);
  
  let updatedContext = context;
  
  for (let i = 0; i < wrappedLines.length; i++) {
    updatedContext = ensurePageSpace(updatedContext);
    
    if (i === 0) {
      updatedContext.page.drawText('-', {
        x: PDF_CONFIG.margin,
        y: updatedContext.currentY,
        size: PDF_CONFIG.fontSize,
        font: updatedContext.font
      });
    }
    
    updatedContext.page.drawText(wrappedLines[i], {
      x: PDF_CONFIG.margin + 15,
      y: updatedContext.currentY,
      size: PDF_CONFIG.fontSize,
      font: updatedContext.font
    });
    
    updatedContext.currentY -= PDF_CONFIG.lineHeight;
  }
  
  return updatedContext;
}

/**
 * Renders a numbered list item.
 */
function drawNumberedLine(context: PDFContext, line: string): PDFContext {
  const match = line.match(/^\s*(\d+)\.\s+(.*)$/);
  if (!match) return drawRegularLine(context, line);
  
  const [, number, content] = match;
  const maxWidth = PDF_CONFIG.pageSize[0] - (PDF_CONFIG.margin * 2) - 25;
  const numberWidth = context.font.widthOfTextAtSize(`${number}.`, PDF_CONFIG.fontSize);
  const wrappedLines = wrapTextToLines(content, maxWidth, context.font, PDF_CONFIG.fontSize);
  
  let updatedContext = context;
  
  for (let i = 0; i < wrappedLines.length; i++) {
    updatedContext = ensurePageSpace(updatedContext);
    
    if (i === 0) {
      updatedContext.page.drawText(`${number}.`, {
        x: PDF_CONFIG.margin,
        y: updatedContext.currentY,
        size: PDF_CONFIG.fontSize,
        font: updatedContext.font
      });
    }
    
    updatedContext.page.drawText(wrappedLines[i], {
      x: PDF_CONFIG.margin + numberWidth + 5,
      y: updatedContext.currentY,
      size: PDF_CONFIG.fontSize,
      font: updatedContext.font
    });
    
    updatedContext.currentY -= PDF_CONFIG.lineHeight;
  }
  
  return updatedContext;
}

/**
 * Renders a line that contains bold markdown segments.
 */
function drawBoldLine(context: PDFContext, line: string): PDFContext {
  const maxWidth = PDF_CONFIG.pageSize[0] - (PDF_CONFIG.margin * 2);
  const wrappedLines = wrapTextToLines(line, maxWidth, context.font, PDF_CONFIG.fontSize);
  
  let updatedContext = context;
  
  for (const wrappedLine of wrappedLines) {
    updatedContext = ensurePageSpace(updatedContext);
    const parts = wrappedLine.split(/(\*\*.*?\*\*)/);
    let x = PDF_CONFIG.margin;
    
    for (const part of parts) {
      if (/^\*\*(.*?)\*\*$/.test(part)) {
        const text = part.replace(/\*\*/g, '');
        updatedContext.page.drawText(text, {
          x,
          y: updatedContext.currentY,
          size: PDF_CONFIG.fontSize,
          font: updatedContext.fontBold
        });
        x += updatedContext.fontBold.widthOfTextAtSize(text, PDF_CONFIG.fontSize);
      } else if (part) {
        updatedContext.page.drawText(part, {
          x,
          y: updatedContext.currentY,
          size: PDF_CONFIG.fontSize,
          font: updatedContext.font
        });
        x += updatedContext.font.widthOfTextAtSize(part, PDF_CONFIG.fontSize);
      }
    }
    updatedContext.currentY -= PDF_CONFIG.lineHeight;
  }
  
  return updatedContext;
}

/**
 * Renders a plain text line.
 */
function drawRegularLine(context: PDFContext, line: string): PDFContext {
  const maxWidth = PDF_CONFIG.pageSize[0] - (PDF_CONFIG.margin * 2);
  const wrappedLines = wrapTextToLines(line, maxWidth, context.font, PDF_CONFIG.fontSize);
  
  let updatedContext = context;
  
  for (const wrappedLine of wrappedLines) {
    updatedContext = ensurePageSpace(updatedContext);
    updatedContext.page.drawText(wrappedLine, {
      x: PDF_CONFIG.margin,
      y: updatedContext.currentY,
      size: PDF_CONFIG.fontSize,
      font: updatedContext.font
    });
    updatedContext.currentY -= PDF_CONFIG.lineHeight;
  }
  
  return updatedContext;
}

/**
 * Processes markdown line by line and draws it using the appropriate renderer.
 */
function processMarkdownText(context: PDFContext, text: string): PDFContext {
  // Sanitize text to avoid characters unsupported by WinAnsi fonts
  const sanitized = sanitizeForPdf(text);
  const lines = sanitized.split(/\n/);
  let updatedContext = context;

  for (let i = 0; i < lines.length; i++) {
    const table = parseMarkdownTable(lines, i);
    if (table) {
      updatedContext = drawMarkdownTable(updatedContext, table.rows);
      i = table.endIndex - 1;
      continue;
    }

    const line = lines[i];
    updatedContext = ensurePageSpace(updatedContext);
    
    if (/^\s*[-*+] /.test(line)) {
      updatedContext = drawBulletLine(updatedContext, line);
    } else if (/^\s*\d+\. /.test(line)) {
      updatedContext = drawNumberedLine(updatedContext, line);
    } else if (/\*\*(.*?)\*\*/.test(line)) {
      updatedContext = drawBoldLine(updatedContext, line);
    } else {
      updatedContext = drawRegularLine(updatedContext, line);
    }
  }
  
  return updatedContext;
}

/**
 * Draws the sender header for a message.
 */
function drawMessageHeader(context: PDFContext, role: string): PDFContext {
  const sender = role === 'user' ? 'User:' : 'Assistant:';
  const updatedContext = ensurePageSpace(context);
  
  updatedContext.page.drawText(sender, {
    x: PDF_CONFIG.margin,
    y: updatedContext.currentY,
    size: PDF_CONFIG.fontSize,
    font: updatedContext.fontBold,
    color: PDF_CONFIG.colors.sender
  });
  
  return {
    ...updatedContext,
    currentY: updatedContext.currentY - PDF_CONFIG.lineHeight
  };
}

/**
 * Renders a separator line between messages.
 */
function drawMessageSeparator(context: PDFContext): PDFContext {
  const updatedContext = ensurePageSpace(context);
  
  updatedContext.page.drawText('---', {
    x: PDF_CONFIG.margin,
    y: updatedContext.currentY,
    size: PDF_CONFIG.fontSize - 2,
    font: updatedContext.font,
    color: PDF_CONFIG.colors.separator
  });
  
  return {
    ...updatedContext,
    currentY: updatedContext.currentY - PDF_CONFIG.lineHeight
  };
}

/**
 * Exports the conversation to PDF while preserving basic markdown formatting.
 */
export async function exportChatToPdf(messages: Array<{ role: string; content: string }>) {
  if (typeof window === 'undefined') {
    return;
  }

  const context = await createPdfDocument();
  let updatedContext = context;
  
  for (const message of messages) {
    updatedContext = drawMessageHeader(updatedContext, message.role);
    updatedContext = processMarkdownText(updatedContext, message.content);
    updatedContext.currentY -= PDF_CONFIG.lineHeight / 2;
    updatedContext = drawMessageSeparator(updatedContext);
    if (updatedContext.currentY < PDF_CONFIG.minY) {
      const newPage = updatedContext.document.addPage(PDF_CONFIG.pageSize);
      updatedContext = {
        ...updatedContext,
        page: newPage,
        currentY: PDF_CONFIG.initialY
      };
    }
  }

  const pdfBytes = await updatedContext.document.save();
  const blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);

  const link = window.document.createElement('a');
  link.href = url;
  link.download = `chat-phyto-${new Date().toISOString().slice(0, 10)}.pdf`;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);

  window.URL.revokeObjectURL(url);
}
